import { useState } from 'react';

import { Playground } from '../../../components/playground/Playground';
import { PlaygroundClasses } from '../../../components/playground/PlaygroundClasses';
import { Description } from '../../../components/Description';
import { Headline } from '../../../components/Headline';
import { Note } from '../../../components/Note';
import Resizer from '@rio-cloud/rio-uikit/Resizer';

import { dummyTextLong } from '../../../utils/data';

export const Ellipsis = () => (
    <div>
        <Headline anchor='ellipsis'>Ellipsis</Headline>
        <Description>
            <p>Single- or multi-line utility to truncate text content at max width with ...</p>
            <Note>
                Ellipsis only works with block elements. It does not work with
                <code className='text-decoration-line-through'>display-grid</code>. Combine the classes with
                <code>display-block</code> or <code>display-flex</code>
            </Note>
        </Description>
        <Playground example={ellipsisExample} classes={ellipsisClasses} />

        <Headline anchor='ellipsis-middle' headlineType='h4'>
            Ellipsis-middle
        </Headline>
        <Description className='margin-top-25'>
            <p>
                The class <code>ellipsis-middle</code> expects two child elements where the first child is the start and
                the second child is the end that should always remain visible.
            </p>
        </Description>
        <Playground example={<MiddleEllipsisExample />} classes={middleEllipsisClasses} />
    </div>
);

const ellipsisExample = (
    <div>
        <p className='ellipsis-1 text-size-16'>{`${dummyTextLong}`}</p>
        <hr />
        <p className='ellipsis-2 text-size-16'>{`${dummyTextLong}`}</p>
        <hr />
        <p className='ellipsis-3 text-size-16'>{`${dummyTextLong}`}</p>
    </div>
);

const MIN_WIDTH = 220;
const MAX_WIDTH = 520;
const INITIAL_WIDTH = 350;

const longId = '2bb5e796-6c5e-4bc4-871b-f3af-d469-1c1e';

const MiddleEllipsisExample = () => {
    const [contentWidth, setContentWidth] = useState(INITIAL_WIDTH);

    const onResize = (diff: number) => {
        setContentWidth(currentWidth => {
            const updatedWidth = currentWidth - diff;
            if (updatedWidth < MIN_WIDTH) {
                return MIN_WIDTH;
            }
            if (updatedWidth > MAX_WIDTH) {
                return MAX_WIDTH;
            }
            return updatedWidth;
        });
    };

    return (
        <div className='max-width-500 overflow-auto padding-5'>
            <div className='position-relative border rounded padding-15' style={{ width: contentWidth }}>
                <div className='space-y-15'>
                    <div className='ellipsis-middle border rounded padding-x-10 padding-y-5 text-size-16'>
                        <span>{longId.slice(0, -6)}</span>
                        <span>{longId.slice(-6)}</span>
                    </div>
                    <div className='ellipsis-middle border rounded padding-x-10 padding-y-5 text-size-16'>
                        <span>{longId.slice(0, -9)}</span>
                        <span>{longId.slice(-9)}</span>
                    </div>
                    <div className='ellipsis-middle border rounded padding-x-10 padding-y-5 text-size-16'>
                        <span>{longId.slice(0, -12)}</span>
                        <span>{longId.slice(-12)}</span>
                    </div>
                </div>

                <Resizer
                    className='right-0 display-grid place-items-center width-auto height-100pct padding-x-2'
                    position={Resizer.RIGHT}
                    onResize={onResize}
                >
                    <div className='height-30 width-2 bg-gray' />
                </Resizer>
            </div>
        </div>
    );
};

const ellipsisClasses = (
    <PlaygroundClasses
        classes={[
            'ellipsis-1',
            'ellipsis-2',
            'ellipsis-3',
            'ellipsis-4',
            'ellipsis-5',
            'ellipsis-6',
            'ellipsis-7',
            'ellipsis-8',
            'ellipsis-9',
            'ellipsis-10',
        ]}
    />
);

const middleEllipsisClasses = <PlaygroundClasses classes={['ellipsis-middle']} />;
